"use client";

import React, { useEffect, useState } from "react";
import { cn, withUrl } from "@/lib/utils";
import Image from "next/image";
import { motion } from "framer-motion";
import LINKS from "@/lib/links";
import SecondaryButton from "../ui/secondary-button";
import { useWindowSize } from "@/hooks/use-window-size";

export interface ImageInfo {
  url: string;
  alt: string;
}

export interface CareersAboutUsProps {
  id?: string;
  className?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  // Single image set for all device sizes with responsive layout
  images?: {
    topLeftImages?: ImageInfo[]; // 1st column, 1st row
    topRightImages?: ImageInfo[]; // 2nd column, 1st row
    bottomLeftImages?: ImageInfo[]; // 1st column, 2nd row
    bottomRightImages?: ImageInfo[]; // 2nd column, 2nd row
    mobileFullHeightImages?: ImageInfo[]; // Mobile-only: 1st column spanning both rows
  };
  // Transition configurations for each image block
  transitions?: {
    topLeftDelay?: number; // Delay in ms for top left images
    topRightDelay?: number; // Delay in ms for top right images
    bottomLeftDelay?: number; // Delay in ms for bottom left images
    bottomRightDelay?: number; // Delay in ms for bottom right images
    mobileFullHeightDelay?: number; // Delay in ms for mobile full height images

    topLeftDuration?: number; // Duration in ms for top left images transition
    topRightDuration?: number; // Duration in ms for top right images transition
    bottomLeftDuration?: number; // Duration in ms for bottom left images transition
    bottomRightDuration?: number; // Duration in ms for bottom right images transition
    mobileFullHeightDuration?: number; // Duration in ms for mobile full height images transition

    duration?: number; // Duration in ms for all transitions
  };
}

const defaultImages = {
  topLeftImages: [
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fd402e858a98446f896176380cb621fc0",
      alt: "Care Indeed team member - top left",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F3b4a499cbca54965bf7393e9dd5d1b1d",
      alt: "Care Indeed team member - top left alternate",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F5e3a5d55927a4ed4b496e6bfecc25f63",
      alt: "Care Indeed team member - top left alternate 2",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F2712358d264b4ee3a539dbb1a31b798a",
      alt: "Care Indeed team member - top left alternate 3",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F76eef15a17d84cb79e20f941703619ed",
      alt: "Care Indeed team member - top left alternate 4",
    },
  ],
  topRightImages: [
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F9d6da23da3f8481894837e96c54de45d",
      alt: "Care Indeed team celebration - top right",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fb992a5ddbfe24545a97eaff75244d2bb",
      alt: "Care Indeed team celebration - top right alternate",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F769388f7697844e7bf92cd43b2b6e630",
      alt: "Care Indeed team celebration - top right alternate 2",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F8dd2312cd8404fb2acd46929aca1f0ed",
      alt: "Care Indeed team celebration - top right alternate 3",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fa072cabdaf124e4eb77d6f54537c6ca7",
      alt: "Care Indeed team celebration - top right alternate 4",
    },
  ],
  bottomLeftImages: [
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F5c67d996f8ff40839956dfdbb1e8ec56",
      alt: "Care Indeed team meeting - bottom left",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F32edfe91fe964d0a93b6e768ae7e4f24",
      alt: "Care Indeed team meeting - bottom left alternate",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F6a425c09deb84f1dbf5c727dbc4def4d",
      alt: "Care Indeed team meeting - bottom left alternate 2",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F5f8c53f026fc48a7bbb624ecb8db119d",
      alt: "Care Indeed team meeting - bottom left alternate 3",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fa83ab90dc22d4feaa1c9ddbf5e2bfe75",
      alt: "Care Indeed team meeting - bottom left alternate 4",
    },
  ],
  bottomRightImages: [
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F628704252c324aebb447df1ad223f6e5",
      alt: "Care Indeed caregivers - bottom right",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F6ece3106bec848ec9a329d5c9b5d839a",
      alt: "Care Indeed caregivers - bottom right alternate",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fc79a425a3f2c48069a78171d054d29c3",
      alt: "Care Indeed caregivers - bottom right alternate 2",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F58a863d8278c40988aa1ca9f21e5fa07",
      alt: "Care Indeed caregivers - bottom right alternate 3",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F78a9a753589c43b1bbd79aaff9a42267",
      alt: "Care Indeed caregivers - bottom right alternate 4",
    },
  ],
  mobileFullHeightImages: [
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Feb52abac2af24184abebbf56e855ac3e",
      alt: "Care Indeed mobile full height image",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Feb5ed1aea43c4ed2abafc0f2b1cc677b",
      alt: "Care Indeed mobile full height image alternate",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F79cf95d8357a42979f4425ba51848c4a",
      alt: "Care Indeed mobile full height image alternate 2",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fa761867ae8054613b7f49d03058ce243",
      alt: "Care Indeed mobile full height image alternate 3",
    },
    {
      url: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2Fe8f0f0286a0e4fa0bedb918e1292a431",
      alt: "Care Indeed mobile full height image alternate 4",
    },
  ],
};

const defaultTransitions = {
  topLeftDelay: 2000,
  topRightDelay: 1500,
  bottomLeftDelay: 1300,
  bottomRightDelay: 1000,
  mobileFullHeightDelay: 1700,

  topLeftDuration: 800,
  topRightDuration: 820,
  bottomLeftDuration: 830,
  bottomRightDuration: 810,
  mobileFullHeightDuration: 840,

  duration: 800, // Fallback if individual durations not specified
};

const defaultProps: CareersAboutUsProps = {
  id: "careers-about-us",
  title: "Care Indeed: A Legacy of Compassionate Care",
  subtitle: "Committed to Excellence in Senior Home Care",
  description:
    "Founded in 2010 by two experienced nurses, Care Indeed has grown into a leading provider of in-home care services in the Bay Area. With a team of over 700 dedicated professionals across four offices, we are consistently recognized as one of the Top 50 Best Companies to Work For. Our core values—excellence, respect, and authenticity—guide our mission to deliver unparalleled care to our clients and a supportive environment for our employees.",
  images: defaultImages,
  transitions: defaultTransitions,
};

// Custom image transition component similar to ImageTransitionBlock
const ImageTransition = ({
  images,
  transitionDelay = 1500,
  transitionDuration = 800,
  className,
}: {
  images: ImageInfo[];
  transitionDelay?: number;
  transitionDuration?: number;
  className?: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [dynamicDelay, setDynamicDelay] = useState(transitionDelay);

  useEffect(() => {
    const preloadImages = async () => {
      const loadPromises = images.map((image) => {
        return new Promise<boolean>((resolve) => {
          const img = document.createElement("img");
          img.src = image.url;
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
        });
      });

      const results = await Promise.all(loadPromises);
      setImagesLoaded(results);
    };

    preloadImages();
  }, [images]);

  useEffect(() => {
    if (imagesLoaded.every(Boolean)) {
      const initialTimer = setTimeout(() => {
        setHasStarted(true);
      }, transitionDelay);

      return () => clearTimeout(initialTimer);
    }
  }, [imagesLoaded, transitionDelay]);

  useEffect(() => {
    if (imagesLoaded.every(Boolean) && hasStarted && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
        setDynamicDelay((prevDelay) => prevDelay + 500);
      }, dynamicDelay);

      return () => clearInterval(interval);
    }
  }, [imagesLoaded, images.length, hasStarted, dynamicDelay, transitionDelay]);

  // Don't render if there are no images
  if (!images || images.length === 0) return null;

  return (
    <div className={`relative overflow-hidden rounded-2xl ${className}`}>
      {images.map((image, index) => (
        <motion.div
          key={index}
          style={{
            position: "absolute",
            inset: 0,
            height: "100%",
            width: "100%",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: index === currentIndex ? 1 : 0 }}
          transition={{
            duration: transitionDuration / 1000,
            ease: "easeOut",
          }}
        >
          <Image
            src={image.url}
            alt={image.alt}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            className="object-cover"
            priority={index === 0}
            loading={index === 0 ? "eager" : "lazy"}
          />
        </motion.div>
      ))}
    </div>
  );
};

const CareersAboutUs: React.FC<CareersAboutUsProps> = ({
  id = defaultProps.id,
  className,
  title = defaultProps.title,
  subtitle = defaultProps.subtitle,
  description = defaultProps.description,
  images = defaultProps.images,
  transitions = defaultProps.transitions,
}) => {
  const size = useWindowSize();

  // Extract images
  const topLeftImages = images?.topLeftImages || [];
  const topRightImages = images?.topRightImages || [];
  const bottomLeftImages = images?.bottomLeftImages || [];
  const bottomRightImages = images?.bottomRightImages || [];
  const mobileFullHeightImages = images?.mobileFullHeightImages || [];

  // Extract transition configs with fallbacks
  const {
    topLeftDelay = defaultTransitions.topLeftDelay,
    topRightDelay = defaultTransitions.topRightDelay,
    bottomLeftDelay = defaultTransitions.bottomLeftDelay,
    bottomRightDelay = defaultTransitions.bottomRightDelay,
    mobileFullHeightDelay = defaultTransitions.mobileFullHeightDelay,

    topLeftDuration = defaultTransitions.topLeftDuration,
    topRightDuration = defaultTransitions.topRightDuration,
    bottomLeftDuration = defaultTransitions.bottomLeftDuration,
    bottomRightDuration = defaultTransitions.bottomRightDuration,
    mobileFullHeightDuration = defaultTransitions.mobileFullHeightDuration,

    duration = defaultTransitions.duration,
  } = transitions || {};

  // Ensure all images are defined
  const hasImages =
    topLeftImages.length > 0 &&
    topRightImages.length > 0 &&
    bottomLeftImages.length > 0 &&
    bottomRightImages.length > 0;

  // Ensure mobile full height images are defined
  const hasMobileFullHeightImages = mobileFullHeightImages.length > 0;

  return (
    <section
      id={id}
      aria-label="About Us Header"
      className={cn("w-full bg-primary-100", className)}
    >
      <div className="flex flex-col gap-6 p-4 md:gap-8 md:px-6 md:py-8 lg:mx-auto lg:max-w-lg lg:gap-10 lg:px-3 lg:py-10">
        <div className="flex flex-col gap-8 md:justify-between lg:flex-row">
          <div className="flex flex-col md:max-w-[710px]">
            <h3 className="mb-2 font-montserrat text-heading-4 text-secondary-500 md:mb-4 md:text-heading-2">
              {title}
            </h3>
            <h2 className="mb-4 font-montserrat text-subheading-3-400 text-neutral-500 md:mb-6">
              {subtitle}
            </h2>
            <div
              className="mb-4 font-roboto text-body-1-300 text-neutral-500 md:mb-6"
              dangerouslySetInnerHTML={{ __html: description || "" }}
            />
            <SecondaryButton
              className="w-fit"
              color="gray"
              href={withUrl(LINKS.aboutUs.href)}
            >
              Learn More About Us
            </SecondaryButton>
          </div>

          {hasImages && (
            <div className="flex w-full flex-col justify-center md:h-[364px] lg:h-[364px] lg:w-[514px]">
              {/* Mobile layout */}
              {size === "sm" && (
                <div className="flex h-[264px] w-full flex-row justify-center gap-2">
                  {/* Mobile full height column (left) */}
                  {hasMobileFullHeightImages && (
                    <div className="relative aspect-[175/264] h-[264px] max-h-[264px] min-h-[264px] w-[175px] min-w-[175px] max-w-[175px]">
                      <ImageTransition
                        images={mobileFullHeightImages}
                        transitionDelay={mobileFullHeightDelay}
                        transitionDuration={
                          mobileFullHeightDuration || duration
                        }
                        className="aspect-[175/264] h-[264px] max-h-[264px] min-h-[264px] w-[175px] min-w-[175px] max-w-[175px]"
                      />
                    </div>
                  )}

                  {/* Mobile right column with two rows */}
                  <div className="flex h-[264px] max-h-[264px] min-h-[264px] w-[175px] min-w-[175px] max-w-[175px] flex-col gap-2">
                    {/* Top row - using desktop top left image */}
                    <div className="relative aspect-[175/128] h-[128px] max-h-[128px] min-h-[128px] w-[175px] min-w-[175px] max-w-[175px]">
                      <ImageTransition
                        images={topLeftImages}
                        transitionDelay={topLeftDelay}
                        transitionDuration={topLeftDuration || duration}
                        className="aspect-[175/128] h-[128px] max-h-[128px] min-h-[128px] w-[175px] min-w-[175px] max-w-[175px]"
                      />
                    </div>
                    {/* Bottom row - using desktop bottom right image */}
                    <div className="relative aspect-[175/128] h-[128px] max-h-[128px] min-h-[128px] w-[175px] min-w-[175px] max-w-[175px]">
                      <ImageTransition
                        images={bottomRightImages}
                        transitionDelay={bottomRightDelay}
                        transitionDuration={bottomRightDuration || duration}
                        className="aspect-[175/128] h-[128px] max-h-[128px] min-h-[128px] w-[175px] min-w-[175px] max-w-[175px]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Tablet layout (md) - 2x2 grid with equal aspect ratios */}
              {size === "md" && (
                <div className="flex h-[364px] max-h-[364px] min-h-[364px] w-full flex-col gap-4">
                  <div className="flex w-full justify-center gap-4">
                    <div className="relative aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]">
                      <ImageTransition
                        images={topLeftImages}
                        transitionDelay={topLeftDelay}
                        transitionDuration={topLeftDuration || duration}
                        className="aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]"
                      />
                    </div>
                    <div className="relative aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]">
                      <ImageTransition
                        images={topRightImages}
                        transitionDelay={topRightDelay}
                        transitionDuration={topRightDuration || duration}
                        className="aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]"
                      />
                    </div>
                  </div>
                  <div className="flex w-full justify-center gap-4">
                    <div className="relative aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]">
                      <ImageTransition
                        images={bottomLeftImages}
                        transitionDelay={bottomLeftDelay}
                        transitionDuration={bottomLeftDuration || duration}
                        className="aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]"
                      />
                    </div>
                    <div className="relative aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]">
                      <ImageTransition
                        images={bottomRightImages}
                        transitionDelay={bottomRightDelay}
                        transitionDuration={bottomRightDuration || duration}
                        className="aspect-[340/172] h-[172px] max-h-[172px] min-h-[172px] w-[340px] min-w-[340px] max-w-[340px]"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop layout (lg) - 2x2 grid with different aspect ratios */}
              {size === "lg" && (
                <div className="flex h-[364px] max-h-[364px] min-h-[364px] w-[514px] min-w-[514px] max-w-[514px] flex-col gap-4">
                  <div className="flex w-full gap-4">
                    <div className="relative aspect-[196/172] h-[172px] max-h-[172px] min-h-[172px] w-[196px] min-w-[196px] max-w-[196px]">
                      <ImageTransition
                        images={topLeftImages}
                        transitionDelay={topLeftDelay}
                        transitionDuration={topLeftDuration || duration}
                        className="aspect-[196/172] h-[172px] max-h-[172px] min-h-[172px] w-[196px] min-w-[196px] max-w-[196px]"
                      />
                    </div>
                    <div className="relative aspect-[302/172] h-[172px] max-h-[172px] min-h-[172px] w-[302px] min-w-[302px] max-w-[302px]">
                      <ImageTransition
                        images={topRightImages}
                        transitionDelay={topRightDelay}
                        transitionDuration={topRightDuration || duration}
                        className="aspect-[302/172] h-[172px] max-h-[172px] min-h-[172px] w-[302px] min-w-[302px] max-w-[302px]"
                      />
                    </div>
                  </div>
                  <div className="flex w-full gap-4">
                    <div className="relative aspect-[302/172] h-[172px] max-h-[172px] min-h-[172px] w-[302px] min-w-[302px] max-w-[302px]">
                      <ImageTransition
                        images={bottomLeftImages}
                        transitionDelay={bottomLeftDelay}
                        transitionDuration={bottomLeftDuration || duration}
                        className="aspect-[302/172] h-[172px] max-h-[172px] min-h-[172px] w-[302px] min-w-[302px] max-w-[302px]"
                      />
                    </div>
                    <div className="relative aspect-[196/172] h-[172px] max-h-[172px] min-h-[172px] w-[196px] min-w-[196px] max-w-[196px]">
                      <ImageTransition
                        images={bottomRightImages}
                        transitionDelay={bottomRightDelay}
                        transitionDuration={bottomRightDuration || duration}
                        className="aspect-[196/172] h-[172px] max-h-[172px] min-h-[172px] w-[196px] min-w-[196px] max-w-[196px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CareersAboutUs;
